import { kebabCase } from 'lodash';
import moment from 'moment';
import { pascalCase } from 'utils/pascal-case';
import { unreachable } from 'utils/unreachable';
import { TermSeparator } from './separators';
export var DateFieldFilterOperationType = /*#__PURE__*/ function(DateFieldFilterOperationType) {
    DateFieldFilterOperationType["IsBeforeToday"] = "IsBeforeToday";
    DateFieldFilterOperationType["IsToday"] = "IsToday";
    DateFieldFilterOperationType["IsNotToday"] = "IsNotToday";
    DateFieldFilterOperationType["IsTomorrow"] = "IsTomorrow";
    DateFieldFilterOperationType["IsNotTomorrow"] = "IsNotTomorrow";
    DateFieldFilterOperationType["IsThisWeek"] = "IsThisWeek";
    DateFieldFilterOperationType["IsLastWeek"] = "IsLastWeek";
    DateFieldFilterOperationType["IsNextWeek"] = "IsNextWeek";
    DateFieldFilterOperationType["IsAny"] = "IsAny";
    DateFieldFilterOperationType["IsThisMonth"] = "IsThisMonth";
    DateFieldFilterOperationType["IsLastMonth"] = "IsLastMonth";
    DateFieldFilterOperationType["IsThisQuarter"] = "IsThisQuarter";
    DateFieldFilterOperationType["IsLastQuarter"] = "IsLastQuarter";
    DateFieldFilterOperationType["IsThisYear"] = "IsThisYear";
    // Relative
    DateFieldFilterOperationType["IsMoreThan"] = "IsMoreThan";
    DateFieldFilterOperationType["IsExactly"] = "IsExactly";
    DateFieldFilterOperationType["IsLessThan"] = "IsLessThan";
    // Absolute
    DateFieldFilterOperationType["IsAfter"] = "IsAfter";
    DateFieldFilterOperationType["IsOn"] = "IsOn";
    DateFieldFilterOperationType["IsBefore"] = "IsBefore";
    // Optional
    DateFieldFilterOperationType["IsEmpty"] = "IsEmpty";
    DateFieldFilterOperationType["IsNotEmpty"] = "IsNotEmpty";
    return DateFieldFilterOperationType;
}({});
export const DateFieldFilterDefaultOperationTypes = [
    "IsBeforeToday",
    "IsToday",
    "IsTomorrow",
    "IsThisWeek",
    "IsLastWeek",
    "IsNextWeek",
    "IsThisMonth",
    "IsLastMonth",
    "IsThisYear",
    "IsThisQuarter",
    "IsLastQuarter",
    "IsAny"
];
export const DateFieldFilterRelativeOperationTypes = [
    "IsMoreThan",
    "IsExactly",
    "IsLessThan"
];
export const DateFieldFilterAbsoluteOperationTypes = [
    "IsAfter",
    "IsOn",
    "IsBefore"
];
export const DateFieldFilterOptionalOperationTypes = [
    "IsEmpty",
    "IsNotEmpty"
];
export const DateFieldFilterOperationTypes = [
    ...DateFieldFilterDefaultOperationTypes,
    ...DateFieldFilterRelativeOperationTypes,
    ...DateFieldFilterAbsoluteOperationTypes,
    ...DateFieldFilterOptionalOperationTypes
];
export const isOptional = (type)=>DateFieldFilterOptionalOperationTypes.includes(type);
export const isDefault = (type)=>type === "IsBeforeToday" || type === "IsToday" || type === "IsTomorrow" || type === "IsThisWeek" || type === "IsNextWeek" || type === "IsThisMonth" || type === "IsAny" || type === "IsMoreThan" || type === "IsExactly" || type === "IsLessThan" || type === "IsAfter" || type === "IsOn" || type === "IsBefore" || type === "IsEmpty" || type === "IsNotEmpty";
export var RelativeDirection = /*#__PURE__*/ function(RelativeDirection) {
    RelativeDirection["Ago"] = "Ago";
    RelativeDirection["FromNow"] = "FromNow";
    return RelativeDirection;
}({});
export var ApiRelativeDirection = /*#__PURE__*/ function(ApiRelativeDirection) {
    ApiRelativeDirection["Ago"] = "ago";
    ApiRelativeDirection["After"] = "after";
    return ApiRelativeDirection;
}({});
const isValid = (filter)=>{
    switch(filter.type){
        case "IsAny":
            return false;
        case "IsMoreThan":
        case "IsExactly":
        case "IsLessThan":
            return filter.days != null;
        case "IsAfter":
        case "IsOn":
        case "IsBefore":
            return filter.value != null;
        default:
            return true;
    }
};
const toString = (filter)=>{
    const type = kebabCase(filter.type);
    switch(filter.type){
        case "IsEmpty":
        case "IsNotEmpty":
        case "IsBeforeToday":
        case "IsToday":
        case "IsNotToday":
        case "IsTomorrow":
        case "IsNotTomorrow":
        case "IsThisWeek":
        case "IsLastWeek":
        case "IsNextWeek":
        case "IsThisMonth":
        case "IsLastMonth":
        case "IsThisQuarter":
        case "IsLastQuarter":
        case "IsThisYear":
        case "IsAny":
            return type;
        case "IsMoreThan":
        case "IsExactly":
        case "IsLessThan":
            return [
                type,
                `${JSON.stringify(filter.days)}~${kebabCase(filter.direction)}`
            ].join(TermSeparator);
        case "IsAfter":
        case "IsOn":
        case "IsBefore":
            return [
                type,
                filter.value == null ? 'null' : filter.value.format('YYYY-MM-DD')
            ].join(TermSeparator);
        default:
            return unreachable(filter);
    }
};
const parse = (value, field)=>{
    const [type_string, meta] = value.split(TermSeparator);
    const type = pascalCase(type_string);
    switch(type){
        case "IsEmpty":
        case "IsNotEmpty":
        case "IsBeforeToday":
        case "IsToday":
        case "IsNotToday":
        case "IsTomorrow":
        case "IsNotTomorrow":
        case "IsThisWeek":
        case "IsLastWeek":
        case "IsNextWeek":
        case "IsThisMonth":
        case "IsLastMonth":
        case "IsThisQuarter":
        case "IsLastQuarter":
        case "IsThisYear":
        case "IsAny":
            return {
                field,
                type
            };
        case "IsMoreThan":
        case "IsExactly":
        case "IsLessThan":
            {
                const [days, direction] = meta.split('~');
                return {
                    field,
                    type,
                    days: JSON.parse(days),
                    direction: pascalCase(direction)
                };
            }
        case "IsAfter":
        case "IsOn":
        case "IsBefore":
            {
                const moment_value = JSON.parse(`"${meta}"`);
                return {
                    field,
                    type,
                    value: moment_value == null ? undefined : moment(moment_value)
                };
            }
        default:
            return unreachable(type);
    }
};
export const DateFieldFilter = {
    isValid,
    toString,
    parse
};
export const isRelativeFilter = (arg)=>{
    return DateFieldFilterRelativeOperationTypes.includes(arg);
};
export const isAbsoluteFilter = (arg)=>{
    return DateFieldFilterAbsoluteOperationTypes.includes(arg);
};
